import { describeCollateral, formatDate, titleCase } from '../BusinessDetailReport/businessReportModel'
import { moreFromLexis } from './moreFromLexis'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const upper = (value) => text(value).toUpperCase()
const dateValue = (date) => (date && typeof date === 'object' && date.Year ? new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime() : 0)
const partyName = (value) => {
    const name = text(value)
    if (!name) return ''
    const [last, first] = name.split(',').map(s => s.trim())
    return titleCase(first && !/\b(INC|LLC|CORP|BANK|CO|LTD|N\.A)\b/i.test(name) ? `${first} ${last}` : name)
}
const unique = (names) => names.filter(Boolean).filter((n, i, all) => all.findIndex(x => x.toUpperCase().replace(/[^A-Z]/g, '') === n.toUpperCase().replace(/[^A-Z]/g, '')) === i)
const cleanCollateral = (value) => text(value).replace(/^\d{1,2}\/\d{1,2}\/\d{4}\s+\S+\s+-\s+/, '')

const asDate = (time) => { const d = new Date(time); return { Year: d.getFullYear(), Month: d.getMonth() + 1, Day: d.getDate() } }

const CHANGE = { AMENDMENT: 'Amended', CONTINUATION: 'Continued', TERMINATION: 'Terminated', ASSIGNMENT: 'Assigned' }
const changeLabel = (type) => CHANGE[Object.keys(CHANGE).find(k => upper(type).startsWith(k))] || titleCase(type) || 'Filed'

export const buildOwnerUcc = (ucc, now = new Date()) => {
    const filings = list(ucc?.Filings?.Filing).filter(f => f && typeof f === 'object')
        .sort((a, b) => dateValue(a.Date) - dateValue(b.Date))
    const filed = ucc?.OriginFilingDate || filings[0]?.Date
    const terminated = filings.find(f => /TERMINAT/.test(upper(f.Type)))
    const continuations = filings.filter(f => /CONTINU/.test(upper(f.Type))).length
    const expiration = filings.map(f => f.ExpirationDate).filter(d => dateValue(d)).sort((a, b) => dateValue(b) - dateValue(a))[0]
    const lapsesAt = dateValue(expiration) || (dateValue(filed) ? new Date(dateValue(filed)).setFullYear(new Date(dateValue(filed)).getFullYear() + 5 * (1 + continuations)) : 0)
    const lapsed = !terminated && lapsesAt && lapsesAt < now.getTime()
    const active = !terminated && !lapsed
    const lenders = unique(list(ucc?.Secureds?.Secured).map(s => partyName(s?.OriginName)))
    const debtors = unique(list(ucc?.Debtors?.Debtor).map(d => partyName(d?.OriginName)))
    const assignees = unique(list(ucc?.Assignees?.Assignee).map(a => partyName(a?.OriginName)))
    const collateral = describeCollateral(list(ucc?.Collaterals?.Collateral).flatMap(c => cleanCollateral(c?.Description).split(';'))
        .map(part => part.trim()).filter(Boolean))
    const recent = dateValue(filed) && now.getTime() - dateValue(filed) < 365.25 * 86400000
    const latest = filings.filter(f => !/^INITIAL/.test(upper(f.Type))).pop()

    return {
        more: moreFromLexis(ucc, 'UCCFilings/UCCFiling[]'),
        active, filedAt: dateValue(filed), lenderNames: lenders,
        receivables: active && (collateral.receivables || collateral.allAssets), recent: active && recent,
        title: lenders.length ? `${lenders[0]}${lenders.length > 1 ? ` and ${lenders.length - 1} more` : ''}` : 'Lender not listed',
        facts: [filed && `Filed ${formatDate(filed)}`, titleCase(ucc?.FilingJurisdictionName) || ucc?.FilingJurisdiction,
            terminated ? `Terminated ${formatDate(terminated.Date)}` : lapsed ? `Lapsed ${formatDate(asDate(lapsesAt))}`
                : expiration ? `Expires ${formatDate(expiration)}` : ''].filter(Boolean).join(' · '),
        chips: [
            active && recent && { label: 'Recent', tone: 'watch' },
            active && collateral.allAssets && { label: 'All assets', tone: 'watch' },
            active && collateral.receivables && { label: 'Receivables', tone: 'watch' },
            active && collateral.summary && !collateral.allAssets && !collateral.receivables && { label: collateral.summary, tone: 'neutral' },
        ].filter(Boolean),
        details: [
            ['Status', active ? 'Active' : terminated ? `Terminated ${formatDate(terminated.Date)}` : 'Lapsed (expired without a continuation)'],
            ['Latest change', latest ? `${changeLabel(latest.Type)} ${formatDate(latest.Date)}${filings.length > 2 ? ` · ${filings.length - 1} changes` : ''}` : 'None since filing'],
            ['Lenders', lenders.join('; ')],
            ['Debtors', debtors.join('; ')],
            ['Assigned to', assignees.join('; ')],
            ['Collateral', collateral.plain || 'Not stated in the filing'],
            ['Filing number', text(ucc?.OriginFilingNumber || filings[0]?.Number)],
            ['Filing office', list(ucc?.FilingOffices?.Office).map(o => titleCase(o?.FilingAgency)).filter(Boolean).join('; ')],
        ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        blocks: filings.length > 1 ? [{
            title: 'Filing history',
            details: filings.map(f => ({ label: formatDate(f.Date) || 'No date', value: [changeLabel(f.Type), f.Number && `No. ${text(f.Number)}`,
                f.ExpirationDate && `expires ${formatDate(f.ExpirationDate)}`, titleCase(f.FilingStatus)].filter(Boolean).join(' · ') })),
        }] : [],
    }
}

export function buildOwnerUccs(uccs, now = new Date()) {
    const rows = list(uccs).filter(u => u && typeof u === 'object').map(u => buildOwnerUcc(u, now))
    const byDate = (a, b) => b.filedAt - a.filedAt
    const active = rows.filter(r => r.active).sort(byDate)
    const ended = rows.filter(r => !r.active).sort(byDate)
    const lenders = new Set(active.flatMap(r => r.lenderNames.map(name => name.toUpperCase())))
    return {
        rows, active, ended,
        summary: { active: active.length, ended: ended.length, lenders: lenders.size,
            receivables: active.filter(r => r.receivables).length, recent: active.filter(r => r.recent).length },
    }
}
